import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { getMyCertificates, downloadCertificatePdf } from '../../../api/certification';
import './CertificatePreviewModal.css';

export default function CertificatePreviewModal({ open, onClose, certificationId, userCertificateId }) {
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl.split('#')[0]);
        setPdfUrl(null);
      }
      setError(null);
      return;
    }

    const fetchPdf = async () => {
      setLoading(true);
      setError(null);
      try {
        let certId = userCertificateId;

        // If userCertificateId is not directly provided, find it using certificationId
        if (!certId && certificationId) {
          const res = await getMyCertificates();
          const list = res?.data ?? res ?? [];
          const found = list.find(
            (c) => String(c.certificationId).toLowerCase() === String(certificationId).toLowerCase()
          );
          if (found) {
            certId = found.userCertificateId ?? found.id;
          } else {
            throw new Error('Certificate record not found.');
          }
        }

        if (certId) {
          const blob = await downloadCertificatePdf(certId);
          const url = URL.createObjectURL(blob) + '#navpanes=0';
          setPdfUrl(url);
        } else {
          throw new Error('No certificate ID specified.');
        }
      } catch (err) {
        setError(err.message || 'Failed to load certificate preview.');
      } finally {
        setLoading(false);
      }
    };

    fetchPdf();
  }, [open, certificationId, userCertificateId]);

  // Clean up URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl.split('#')[0]);
      }
    };
  }, [pdfUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      style={{ zIndex: 1000000 }}
      PaperProps={{
        style: {
          backgroundColor: '#0c1515',
          color: 'white',
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          overflow: 'hidden',
          padding: 0,
        },
      }}
    >
      {/* Header with Close Button */}
      <div className="cpm-header">
        <h2 className="cpm-title">Certificate Preview</h2>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent sx={{ p: 0, overflow: 'hidden', height: '650px', position: 'relative' }}>
        {loading && (
          <div className="cpm-loading-container">
            <CircularProgress style={{ color: '#00d5b9' }} size={40} />
            <p className="cpm-loading-text">Generating certificate preview...</p>
          </div>
        )}

        {error && (
          <div className="cpm-error-container">
            <div className="cpm-error-icon">⚠️</div>
            <h3>Unable to load certificate</h3>
            <p>{error}</p>
            <button className="cpm-error-close-btn" onClick={onClose}>Close</button>
          </div>
        )}

        {!loading && !error && pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', backgroundColor: '#fff' }}
            title="Certificate PDF Preview"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
